import { memo, useEffect } from 'react';
import { type NodeProps, Handle, Position, NodeResizer, useUpdateNodeInternals } from 'reactflow';
import { GitBranch, Loader2 } from 'lucide-react';

export const ConditionalRouterNode = memo(({ id, data, selected }: NodeProps) => {
    const conditions = data.conditions || [];
    const defaultRoute = data.defaultRoute || 'default';
    const updateNodeInternals = useUpdateNodeInternals();

    // Force React Flow to re-measure handle positions when conditions change
    useEffect(() => {
        updateNodeInternals(id);
    }, [id, conditions.length, updateNodeInternals]);

    // Match ConditionalSplitNode: distributed across the node height
    const totalHandles = conditions.length + 1;
    const getHandleTopPercent = (index: number) => {
        const fraction = (index + 1) / (totalHandles + 1);
        return `${fraction * 100}%`;
    };

    return (
        <div className="relative flex flex-col items-center">
            <NodeResizer
                color="#94a3b8"
                isVisible={selected}
                minWidth={48}
                minHeight={48}
                handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
            />

            {/* Main Node Body */}
            <div
                className={`
                    relative group flex flex-col items-center justify-center p-2
                    min-w-[80px] rounded-xl shadow-sm border-[1.5px] transition-all duration-300 z-10 bg-white
                    ${selected
                        ? 'border-yellow-400 ring-4 ring-yellow-400/10 shadow-xl scale-105'
                        : 'border-yellow-500 bg-yellow-50 hover:border-yellow-600 hover:shadow-md'
                    }
                `}
                style={{ minHeight: Math.max(60, totalHandles * 24 + 20) }}
            >
                <GitBranch className="h-6 w-6 text-yellow-600 mb-1 transition-opacity duration-300" strokeWidth={1.5} />

                {data?.executionStatus === 'Running' && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-xl z-20">
                        <Loader2 className="h-5 w-5 text-yellow-600 animate-spin" />
                    </div>
                )}

                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider text-center max-w-[90px] leading-tight">
                    {data.label || 'Router'}
                </span>

                {/* Input Handle (Left) */}
                <Handle
                    type="target"
                    position={Position.Left}
                    id="in"
                    className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 pointer-events-auto"
                    style={{ left: -6, top: '50%' }}
                />

                {/* Top Handle */}
                <Handle
                    type="target"
                    position={Position.Top}
                    id="in-top"
                    className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ top: -2, left: '50%' }}
                />

                {/* Dynamic Output Handles */}
                <div className="absolute right-[-6px] top-0 bottom-0 flex flex-col justify-around py-2 h-full pointer-events-none">
                    {conditions.map((cond: any, idx: number) => {
                        const routeName = cond.routeName || `route_${idx + 1}`;
                        const handleId = cond.id ? `route-${cond.id}` : routeName;

                        return (
                            <div
                                key={cond.id ?? idx}
                                className="relative flex items-center h-4 group/handle pointer-events-none"
                            >
                                {/* Label */}
                                <span className="absolute right-4 text-[8px] text-gray-400 opacity-0 group-hover/handle:opacity-100 whitespace-nowrap bg-white px-1 border rounded shadow-sm mr-1 transition-opacity">
                                    {routeName}
                                </span>

                                {/* Stats */}
                                {data.outputStats && data.outputStats[handleId] !== undefined && (
                                    <span className="absolute left-full ml-1 text-[8px] font-mono bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                                        {data.outputStats[handleId].toLocaleString()} rows
                                    </span>
                                )}

                                {/* Handle */}
                                <Handle
                                    type="source"
                                    position={Position.Right}
                                    id={handleId}
                                    className="!relative !w-3 !h-3 !bg-white !border-2 !border-yellow-500 z-50 cursor-pointer pointer-events-auto"
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Default Handle (fixed position) */}
                <div
                    className="absolute flex items-center pointer-events-none"
                    style={{
                        right: -6,
                        top: getHandleTopPercent(conditions.length),
                        transform: 'translateY(-50%)'
                    }}
                >
                    {/* Label */}
                    <span className="absolute right-5 text-[8px] text-gray-400 opacity-0 group-hover:opacity-100 whitespace-nowrap bg-white px-1 border rounded shadow-sm mr-1 transition-opacity">
                        {defaultRoute}
                    </span>

                    {/* Stats */}
                    {data.outputStats && data.outputStats['default'] !== undefined && (
                        <span className="absolute left-full ml-1 text-[8px] font-mono bg-slate-100/80 text-slate-600 px-1 rounded border border-slate-200 whitespace-nowrap z-50">
                            {data.outputStats['default'].toLocaleString()} rows
                        </span>
                    )}

                    {/* Handle */}
                    <Handle
                        type="source"
                        position={Position.Right}
                        id="default"
                        className="!w-3 !h-3 !bg-white !border-2 !border-slate-400 z-50 pointer-events-auto"
                    />
                </div>
            </div>
        </div>
    );
});

ConditionalRouterNode.displayName = 'ConditionalRouterNode';
export default ConditionalRouterNode;